<template>
  <div class="comment d-flex mt-3 mb-3">
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div
      class="visibility"
      :class="{ 'user-visibility': isStandardComment(comment) }"
    ></div>
    <div class="eye-icon-container">
      <i
        :class="[
          'fa-regular',
          {
            'fa-eye': comment.customer_visible,
            'fa-eye-slash': !comment.customer_visible
          }
        ]"
      />
    </div>
    <div class="d-flex flex-column full-width comment-container">
      <div class="comment-content my-1 d-flex justify-content-between">
        <div>
          <i
            v-if="comment.pinned"
            data-cy="pinned"
            class="fa-solid fa-thumbtack mr-1 text-primary text-xs"
          />
          <span v-html="comment.comment" />
        </div>
        <CommentActions
          v-if="allowEdit"
          :comment="comment"
          @edit-comment="toggleEdit"
          @delete-comment="handleDelete"
          @toggle-visibility="handleVisible"
          @toggle-important="handleImportant"
        />
      </div>

      <CommentFormModal
        v-if="isEditing"
        :comment="comment"
        :commentable-type="commentableType"
        :commentable-id="commentableId"
        @updated="handleEdit"
        @cancel="toggleEdit"
        @hide-modal="isEditing = false"
      />
      <div class="d-flex align-items-center justify-content-between">
        <div class="comment-header">
          <span class="text-grey" :data-tippy-content="user.email">
            <b>{{ user.full_name }}</b>
          </span>
          <span>·</span>
          <span :data-tippy-content="timestamp.created_long" class="timestamp">
            {{ timestamp.created }}
          </span>
          <span v-if="timestamp.updated && comment.edited">·</span>
          <span
            v-if="timestamp.updated && comment.edited"
            :data-tippy-content="timestamp.updated_long"
            class="timestamp edited"
            >Edited: {{ timestamp.updated }}</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import moment from "moment";

import { Comment } from "@/typings/comment";
import CONSTANTS from "../Constants.js";
import CommentActions from "./CommentActions.vue";
import CommentFormModal from "./CommentFormModal.vue";

export default {
  components: {
    CommentFormModal,
    CommentActions
  },
  emits: [
    "updated-comment",
    "toggle-important",
    "delete-comment",
    "toggle-visibility"
  ],
  props: {
    commentableType: {
      type: String,
      default: ""
    },
    commentableId: {
      type: Number,
      default: null
    },
    comment: {
      type: Object as () => Comment,
      default: () => ({})
    },
    allowEdit: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      isEditing: false,
      commentEdit: this.comment.comment
    };
  },
  computed: {
    timestamp() {
      const { created_at, updated_at } = this.comment;
      let timestamp = {
        updated: undefined as undefined | string,
        updated_long: undefined as undefined | string,
        created: moment(created_at).format(CONSTANTS.TIME_FORMAT),
        created_long: moment(created_at).format(CONSTANTS.DATE_TIME_FORMAT)
      };
      if (created_at !== updated_at) {
        timestamp = {
          ...timestamp,
          updated: moment(updated_at).format(CONSTANTS.TIME_FORMAT),
          updated_long: moment(updated_at).format(CONSTANTS.DATE_TIME_FORMAT)
        };
      }
      return timestamp;
    },
    user() {
      return (
        this.comment.user || {
          id: "anonymous",
          full_name: "System Message",
          email: "Not Available"
        }
      );
    },
    key() {
      return this.comment.message_key;
    }
  },
  methods: {
    toggleEdit() {
      this.isEditing = !this.isEditing;
    },
    handleEdit() {
      this.$emit("updated-comment");
      this.isEditing = false;
    },
    handleVisible() {
      this.$emit(
        "toggle-visibility",
        this.comment.id,
        !this.comment.customer_visible
      );
    },
    handleImportant() {
      this.$emit("toggle-important", this.comment.id, !this.comment.pinned);
    },
    handleDelete() {
      this.$emit("delete-comment", this.comment.id);
    }
  }
};
</script>

<style lang="scss" scoped>
.eye-icon-container {
  font-size: 1.5rem;
  align-items: center;
  justify-content: center;
  display: flex;
  margin: 0 5px 0 15px;
  i {
    color: var(--secondary-color);
  }
}
</style>
